/* eslint-disable no-unused-vars */
import React, { useState, useCallback } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  TextInput,
  Image,
  ScrollView,
  Alert,
  RefreshControl,
  StatusBar,
  ActivityIndicator,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
} from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  vendors: `${API_BASE_URL}/api/data/vendors`,
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const SubcontractorsVendorListScreen = ({ navigation }) => {
  const [activeTab, setActiveTab] = useState('All Subcontractors');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('All Specialties');
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subcontractors, setSubcontractors] = useState([]);
  const [filteredSubcontractors, setFilteredSubcontractors] = useState([]);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [filters, setFilters] = useState({ status: 'All', rating: 'All' });
  const [showFilters, setShowFilters] = useState(false);

  const specialties = [
    'All Specialties',
    'Concrete Work',
    'Electrical',
    'Plumbing',
    'Drywall',
    'Roofing',
    'Carpentry',
    'Flooring',
    'Painting',
    'HVAC',
  ];

  useFocusEffect(
    useCallback(() => {
      loadCompanyLogo();
      fetchSubcontractors();
    }, [])
  );

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data?.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // Transform database vendor object to the shape expected by the UI
  const transformVendor = (v) => ({
    id: v.id,
    name: v.company_name,
    specialization: v.specialization || [],
    ratings: v.ratings || { quality: 0, reliability: 0, cost: 0, time: 0 },
    overall: Number(v.overall) || 0, // ✅ ensure number
    contact: {
      phone: v.phone,
      email: v.email,
      emergencyPhone: v.emergency_phone,
    },
    location: v.city && v.country ? `${v.city}, ${v.country}` : (v.address || 'Unknown'),
    status: v.status,
    teamSize: v.team_size,
    isFavorite: v.is_favorite || false,
    lastUsed: v.last_used,
    projectsCompleted: Number(v.projects_completed) || 0, // ✅ ensure number
    onTimeRate: Number(v.on_time_rate) || 100,            // ✅ ensure number
    contractInfo: v.contract_info || {},
    companyInfo: v.company_info || {},
    availability: v.availability || {},
    recentProjects: v.recent_projects || [],
    notes: v.notes,
  });

  const fetchSubcontractors = async () => {
    setLoading(true);
    try {
      const companyId = await AsyncStorage.getItem('company_id');
      const response = await api.get(API_ENDPOINTS.vendors, {
        params: { company_id: companyId, type: 'subcontractor' },
      });
      let data = [];
      if (response.data?.success && Array.isArray(response.data.data)) {
        data = response.data.data;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      }
      const transformed = data.map(transformVendor);
      setSubcontractors(transformed);
      applyFilters(transformed);
    } catch (error) {
      console.error('Error fetching subcontractors:', error);
      Alert.alert('Error', 'Failed to load subcontractors');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const applyFilters = (data = subcontractors) => {
    let filtered = [...data];

    // Tab filter
    if (activeTab === 'Favorites') {
      filtered = filtered.filter((s) => s.isFavorite);
    } else if (activeTab === 'Recently Used') {
      filtered = filtered.sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed));
    }

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.name?.toLowerCase().includes(q) ||
          s.specialization?.some((spec) => spec.toLowerCase().includes(q)) ||
          s.location?.toLowerCase().includes(q)
      );
    }

    // Specialty
    if (selectedSpecialty !== 'All Specialties') {
      filtered = filtered.filter((s) =>
        s.specialization?.some((spec) => spec.toLowerCase().includes(selectedSpecialty.toLowerCase()))
      );
    }

    // Status filter
    if (filters.status !== 'All') {
      filtered = filtered.filter((s) => s.status === filters.status);
    }

    // Rating filter
    if (filters.rating !== 'All') {
      const min = parseFloat(filters.rating.replace('+', ''));
      filtered = filtered.filter((s) => s.overall >= min);
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'rating') return b.overall - a.overall;
      if (sortBy === 'lastUsed') return new Date(b.lastUsed) - new Date(a.lastUsed);
      return a.name?.localeCompare(b.name);
    });

    setFilteredSubcontractors(filtered);
  };

  React.useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedSpecialty, activeTab, filters, sortBy, subcontractors]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchSubcontractors();
  };

  const toggleFavorite = async (id, currentValue) => {
    try {
      await api.put(`${API_ENDPOINTS.vendors}/${id}`, { is_favorite: !currentValue });
      fetchSubcontractors();
      Alert.alert('Success', 'Favorite updated');
    } catch (error) {
      Alert.alert('Error', 'Could not update favorite');
    }
  };

  const handleCall = (phone) => {
    Alert.alert('Call', `Call ${phone}?`, [
      { text: 'Cancel' },
      { text: 'Call', onPress: () => console.log('Calling', phone) },
    ]);
  };

  const handleEmail = (email) => {
    Alert.alert('Email', `Send email to ${email}?`, [
      { text: 'Cancel' },
      { text: 'Email', onPress: () => console.log('Emailing', email) },
    ]);
  };

  const handleAssign = (item) => {
    navigation.navigate('ProjectAssignment', { subcontractor: item });
  };

  const handleGoBack = () => {
    if (navigation.canGoBack()) navigation.goBack();
  };

  const renderRatingBar = (value) => {
    const percentage = (value / 5) * 100;
    return (
      <View style={styles.ratingBarContainer}>
        <View style={[styles.ratingBar, { width: `${percentage}%` }]} />
      </View>
    );
  };

  const renderSubcontractorCard = ({ item }) => (
    <TouchableOpacity
      style={[styles.subcontractorCard, item.status === 'Inactive' && styles.inactiveCard]}
      onPress={() => navigation.navigate('SubcontractorDetail', { subcontractorId: item.id })}
    >
      <View style={styles.subcontractorHeader}>
        <View style={styles.subcontractorTitleContainer}>
          <Text style={styles.subcontractorName}>{item.name}</Text>
          <TouchableOpacity onPress={() => toggleFavorite(item.id, item.isFavorite)}>
            <Ionicons
              name={item.isFavorite ? 'heart' : 'heart-outline'}
              size={20}
              color={item.isFavorite ? '#ff4757' : '#64748B'}
            />
          </TouchableOpacity>
        </View>
        <View style={styles.statusContainer}>
          <View style={[styles.statusBadge, item.status === 'Active' ? styles.activeStatus : styles.inactiveStatus]}>
            <Text style={styles.statusText}>{item.status}</Text>
          </View>
          {item.onTimeRate >= 90 && (
            <View style={styles.performanceBadge}>
              <Ionicons name="star" size={12} color="#fdb913" />
              <Text style={styles.performanceText}>Top Performer</Text>
            </View>
          )}
        </View>
      </View>

      <Text style={styles.subcontractorSpecialization}>{(item.specialization || []).join(', ')}</Text>

      <View style={styles.subcontractorDetails}>
        <Text style={styles.subcontractorLocation}>
          <Ionicons name="location-outline" size={14} color="#64748B" /> {item.location}
        </Text>
        <Text style={styles.subcontractorTeamSize}>
          <Ionicons name="people-outline" size={14} color="#64748B" /> Team: {item.teamSize}
        </Text>
        <Text style={styles.subcontractorProjects}>
          <MaterialCommunityIcons name="clipboard-check" size={14} color="#64748B" />
          {item.projectsCompleted || 0} projects completed
        </Text>
      </View>

      <View style={styles.ratingsContainer}>
        <View style={styles.overallRating}>
          <Text style={styles.overallRatingValue}>{item.overall?.toFixed(1) || '0.0'}</Text>
          <Text style={styles.overallRatingLabel}>Overall</Text>
        </View>
        <View style={styles.detailedRatings}>
          <View style={styles.ratingItem}>
            <Text style={styles.ratingLabel}>Quality</Text>
            {renderRatingBar(item.ratings?.quality || 0)}
            <Text style={styles.ratingValue}>{(item.ratings?.quality || 0).toFixed(1)}</Text>
          </View>
          <View style={styles.ratingItem}>
            <Text style={styles.ratingLabel}>Reliability</Text>
            {renderRatingBar(item.ratings?.reliability || 0)}
            <Text style={styles.ratingValue}>{(item.ratings?.reliability || 0).toFixed(1)}</Text>
          </View>
          <View style={styles.ratingItem}>
            <Text style={styles.ratingLabel}>Cost</Text>
            {renderRatingBar(item.ratings?.cost || 0)}
            <Text style={styles.ratingValue}>{(item.ratings?.cost || 0).toFixed(1)}</Text>
          </View>
          <View style={styles.ratingItem}>
            <Text style={styles.ratingLabel}>Time</Text>
            {renderRatingBar(item.ratings?.time || 0)}
            <Text style={styles.ratingValue}>{(item.ratings?.time || 0).toFixed(1)}</Text>
          </View>
        </View>
      </View>

      <View style={styles.subcontractorActions}>
        <TouchableOpacity style={styles.actionButton} onPress={() => handleCall(item.contact?.phone)}>
          <Ionicons name="call-outline" size={16} color="#fff" />
          <Text style={styles.actionButtonText}>Call</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.actionButton} onPress={() => handleEmail(item.contact?.email)}>
          <Ionicons name="mail-outline" size={16} color="#fff" />
          <Text style={styles.actionButtonText}>Email</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.actionButton, styles.primaryActionButton]} onPress={() => handleAssign(item)}>
          <MaterialCommunityIcons name="clipboard-text-outline" size={16} color="#fff" />
          <Text style={styles.actionButtonText}>Assign</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderFilterModal = () => {
    if (!showFilters) return null;
    return (
      <View style={styles.filterOverlay}>
        <View style={styles.filterModal}>
          <View style={styles.filterHeader}>
            <Text style={styles.filterTitle}>Filter & Sort</Text>
            <TouchableOpacity onPress={() => setShowFilters(false)}>
              <Ionicons name="close" size={24} color="#0f172a" />
            </TouchableOpacity>
          </View>
          <View style={styles.filterContent}>
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Sort By</Text>
              <View style={styles.filterOptions}>
                {[
                  { key: 'name', label: 'Name' },
                  { key: 'rating', label: 'Rating' },
                  { key: 'lastUsed', label: 'Recently Used' },
                ].map((opt) => (
                  <TouchableOpacity
                    key={opt.key}
                    style={[styles.filterOption, sortBy === opt.key && styles.activeFilterOption]}
                    onPress={() => setSortBy(opt.key)}
                  >
                    <Text style={[styles.filterOptionText, sortBy === opt.key && styles.activeFilterOptionText]}>
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Status</Text>
              <View style={styles.filterOptions}>
                {['All', 'Active', 'Inactive'].map((s) => (
                  <TouchableOpacity
                    key={s}
                    style={[styles.filterOption, filters.status === s && styles.activeFilterOption]}
                    onPress={() => setFilters((prev) => ({ ...prev, status: s }))}
                  >
                    <Text style={[styles.filterOptionText, filters.status === s && styles.activeFilterOptionText]}>
                      {s}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Minimum Rating</Text>
              <View style={styles.filterOptions}>
                {['All', '4.5+', '4.0+', '3.5+'].map((r) => (
                  <TouchableOpacity
                    key={r}
                    style={[styles.filterOption, filters.rating === r && styles.activeFilterOption]}
                    onPress={() => setFilters((prev) => ({ ...prev, rating: r }))}
                  >
                    <Text style={[styles.filterOptionText, filters.rating === r && styles.activeFilterOptionText]}>
                      {r}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>
          <View style={styles.filterActions}>
            <TouchableOpacity
              style={styles.clearFiltersButton}
              onPress={() => {
                setFilters({ status: 'All', rating: 'All' });
                setSortBy('name');
              }}
            >
              <Text style={styles.clearFiltersText}>Clear All</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.applyFiltersButton} onPress={() => setShowFilters(false)}>
              <Text style={styles.applyFiltersText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
        <Ionicons name="chevron-back" size={24} color="#fff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        {companyLogo ? (
          <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
        ) : (
          <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
        )}
        <Text style={styles.headerTitle}>Subcontractors</Text>
      </View>
      <TouchableOpacity style={styles.headerButton} onPress={() => navigation.navigate('VendorPerformanceDashboard')}>
        <MaterialCommunityIcons name="chart-line" size={24} color="#fff" />
      </TouchableOpacity>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      {renderHeader()}

      <View style={styles.tabContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {['All Subcontractors', 'Favorites', 'Recently Used'].map((tab) => (
            <TouchableOpacity
              key={tab}
              style={[styles.tabButton, activeTab === tab && styles.activeTabButton]}
              onPress={() => setActiveTab(tab)}
            >
              <Text style={[styles.tabText, activeTab === tab && styles.activeTabText]}>{tab}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <View style={styles.searchContainer}>
        <View style={styles.searchBar}>
          <Ionicons name="search" size={20} color="#64748B" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search subcontractors..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#94A3B8"
          />
          {searchQuery ? (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color="#64748B" />
            </TouchableOpacity>
          ) : null}
        </View>
        <TouchableOpacity style={styles.filterButton} onPress={() => setShowFilters(true)}>
          <Ionicons name="filter" size={20} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity style={styles.categoriesButton} onPress={() => navigation.navigate('VendorCategories')}>
          <MaterialIcons name="category" size={20} color="#fff" />
        </TouchableOpacity>
      </View>

      <View style={styles.specialtiesContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {specialties.map((spec) => (
            <TouchableOpacity
              key={spec}
              style={[styles.specialtyButton, selectedSpecialty === spec && styles.activeSpecialtyButton]}
              onPress={() => setSelectedSpecialty(spec)}
            >
              <Text style={[styles.specialtyText, selectedSpecialty === spec && styles.activeSpecialtyText]}>
                {spec}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <View style={styles.summaryContainer}>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryValue}>{filteredSubcontractors.length}</Text>
          <Text style={styles.summaryLabel}>Subcontractors</Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryValue}>
            {filteredSubcontractors.filter((s) => s.status === 'Active').length}
          </Text>
          <Text style={styles.summaryLabel}>Active</Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryValue}>
            {filteredSubcontractors.filter((s) => s.isFavorite).length}
          </Text>
          <Text style={styles.summaryLabel}>Favorites</Text>
        </View>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading subcontractors...</Text>
        </View>
      ) : (
        <FlatList
          data={filteredSubcontractors}
          renderItem={renderSubcontractorCard}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={styles.subcontractorList}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <MaterialIcons name="search-off" size={48} color="#CBD5E1" />
              <Text style={styles.emptyTitle}>No subcontractors found</Text>
              <Text style={styles.emptyText}>Try adjusting your search or filters</Text>
              <TouchableOpacity
                style={styles.addButtonEmptyState}
                onPress={() => navigation.navigate('AddEditSubcontractor', { isEdit: false })}
              >
                <Text style={styles.addButtonText}>Add New Subcontractor</Text>
              </TouchableOpacity>
            </View>
          }
        />
      )}

      <TouchableOpacity
        style={styles.floatingButton}
        onPress={() => navigation.navigate('AddEditSubcontractor', { isEdit: false })}
      >
        <Ionicons name="add" size={24} color="#fff" />
      </TouchableOpacity>

      {renderFilterModal()}
    </SafeAreaView>
  );
};

// Styles (unchanged, same as before)
const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#4A90E2' },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: { padding: 5 },
  headerCenter: { flexDirection: 'row', alignItems: 'center' },
  headerLogo: { width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' },
  headerTitle: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginLeft: 10 },
  headerButton: { padding: 5 },
  tabContainer: {
    backgroundColor: '#fff',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tabButton: {
    paddingHorizontal: 15,
    paddingVertical: 8,
    marginHorizontal: 5,
    borderRadius: 4,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  activeTabButton: { backgroundColor: '#fff', borderColor: '#0e4e96' },
  tabText: { color: '#64748B', fontWeight: '500' },
  activeTabText: { color: '#0e4e96', fontWeight: '600' },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 40,
    marginRight: 10,
  },
  searchInput: { flex: 1, fontSize: 16, color: '#0f172a', marginLeft: 8 },
  filterButton: {
    backgroundColor: '#0e4e96',
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  categoriesButton: {
    backgroundColor: '#fdb913',
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  specialtiesContainer: {
    paddingVertical: 10,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  specialtyButton: {
    paddingHorizontal: 15,
    paddingVertical: 8,
    marginHorizontal: 5,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    backgroundColor: '#f1f5f9',
  },
  activeSpecialtyButton: { backgroundColor: '#fff', borderColor: '#0e4e96' },
  specialtyText: { color: '#64748B' },
  activeSpecialtyText: { color: '#0e4e96', fontWeight: '600' },
  summaryContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  summaryItem: { alignItems: 'center' },
  summaryValue: { fontSize: 18, fontWeight: 'bold', color: '#0e4e96' },
  summaryLabel: { fontSize: 12, color: '#64748B', marginTop: 4 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc' },
  loadingText: { marginTop: 10, fontSize: 16, color: '#64748B' },
  subcontractorList: { padding: 12, backgroundColor: '#f8fafc' },
  subcontractorCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  inactiveCard: { opacity: 0.7 },
  subcontractorHeader: { marginBottom: 10 },
  subcontractorTitleContainer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  subcontractorName: { fontSize: 18, fontWeight: '600', color: '#0f172a', flex: 1 },
  statusContainer: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  statusBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4 },
  activeStatus: { backgroundColor: '#10B981' },
  inactiveStatus: { backgroundColor: '#64748B' },
  statusText: { color: '#fff', fontSize: 12, fontWeight: 'bold' },
  performanceBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff5e6',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#fdb913',
  },
  performanceText: { color: '#fdb913', fontSize: 10, fontWeight: 'bold', marginLeft: 2 },
  subcontractorSpecialization: { fontSize: 14, color: '#64748B', marginBottom: 10 },
  subcontractorDetails: { marginBottom: 15 },
  subcontractorLocation: { fontSize: 14, color: '#64748B', marginBottom: 5 },
  subcontractorTeamSize: { fontSize: 14, color: '#64748B', marginBottom: 5 },
  subcontractorProjects: { fontSize: 14, color: '#64748B' },
  ratingsContainer: { flexDirection: 'row', marginBottom: 15 },
  overallRating: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  overallRatingValue: { fontSize: 18, fontWeight: 'bold', color: '#0e4e96' },
  overallRatingLabel: { fontSize: 10, color: '#64748B' },
  detailedRatings: { flex: 1 },
  ratingItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 5 },
  ratingLabel: { width: 70, fontSize: 12, color: '#64748B' },
  ratingBarContainer: { flex: 1, height: 6, backgroundColor: '#e2e8f0', borderRadius: 3, marginHorizontal: 10 },
  ratingBar: { height: '100%', backgroundColor: '#0e4e96', borderRadius: 3 },
  ratingValue: { width: 30, fontSize: 12, fontWeight: 'bold', color: '#0f172a', textAlign: 'right' },
  subcontractorActions: { flexDirection: 'row', justifyContent: 'space-between' },
  actionButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    marginHorizontal: 3,
  },
  primaryActionButton: { backgroundColor: '#fdb913' },
  actionButtonText: { color: '#fff', fontWeight: '600', fontSize: 12, marginLeft: 5 },
  emptyContainer: { alignItems: 'center', justifyContent: 'center', padding: 40, backgroundColor: '#f8fafc' },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a', marginTop: 16, marginBottom: 8 },
  emptyText: { fontSize: 14, color: '#64748B', textAlign: 'center', marginBottom: 24 },
  addButtonEmptyState: { backgroundColor: '#0e4e96', paddingVertical: 12, paddingHorizontal: 20, borderRadius: 8 },
  addButtonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
  floatingButton: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#0f172a',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 5,
    elevation: 5,
  },
  filterOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
    zIndex: 1000,
  },
  filterModal: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
  },
  filterHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  filterTitle: { fontSize: 18, fontWeight: 'bold', color: '#0f172a' },
  filterContent: { padding: 20 },
  filterSection: { marginBottom: 25 },
  filterSectionTitle: { fontSize: 16, fontWeight: 'bold', color: '#0f172a', marginBottom: 10 },
  filterOptions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  filterOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    backgroundColor: '#f1f5f9',
  },
  activeFilterOption: { backgroundColor: '#fff', borderColor: '#0e4e96' },
  filterOptionText: { fontSize: 14, color: '#64748B' },
  activeFilterOptionText: { color: '#0e4e96', fontWeight: '600' },
  filterActions: { flexDirection: 'row', padding: 20, borderTopWidth: 1, borderTopColor: '#e2e8f0', gap: 10 },
  clearFiltersButton: { flex: 1, backgroundColor: 'transparent', borderWidth: 1, borderColor: '#0e4e96', paddingVertical: 12, borderRadius: 8, alignItems: 'center' },
  clearFiltersText: { color: '#0e4e96', fontWeight: '600' },
  applyFiltersButton: { flex: 1, backgroundColor: '#0e4e96', paddingVertical: 12, borderRadius: 8, alignItems: 'center' },
  applyFiltersText: { color: '#fff', fontWeight: '600' },
});

export default SubcontractorsVendorListScreen;